// frontend/src/pages/dashboard/ProjectComponents/ProjectCard.js
import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Card, CardHeader, CardContent, CardActions, Box, Grid, Typography, Chip, IconButton, Tooltip, Badge, Collapse
} from "@mui/material";
import {
  CalendarToday as CalendarIcon, AccessTime as TimeIcon, Business as BusinessIcon,
  AccountBalance as BillingIcon, Group as GroupIcon, AttachFile as AttachFileIcon,
  Timeline as TimelineIcon, Dashboard as DashboardIcon, Edit as EditIcon,
  Delete as DeleteIcon, Visibility as ViewIcon, ChatBubbleOutline as ChatBubbleOutlineIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon
} from "@mui/icons-material";
import { convertToCompanyTime } from "../../../utils/time";
import ProjectCommentSection from './ProjectCommentSection';
import { getAvatarUrl } from '../../../utils/avatarUtils';

const formatBillingInfo = (project) => {
  if (!project) return "N/A";
  const currency = project.currency || "USD";
  switch (project.billing_model) {
    case "hourly": return `${project.hourly_rate || "0"} ${currency}/hr`;
    case "fixed": return `${project.fixed_price || "0"} ${currency} (Fixed)`;
    case "monthly": return `${project.fixed_price || "0"} ${currency}/month`;
    case "quarter": return `${project.fixed_price || "0"} ${currency}/quarter`;
    case "half-yearly": return `${project.fixed_price || "0"} ${currency}/6 months`;
    case "yearly": return `${project.fixed_price || "0"} ${currency}/year`;
    case "mixed": return `Mixed (${project.hourly_rate || "0"}/hr + ${project.fixed_price || "0"} fixed)`;
    case "not-applicable": default: return "Not Applicable";
  }
};

const ProjectCard = React.memo(({
  project,
  index,
  handlers,
  authUser,
  hasPermission,
  theme,
  company,
  newCommentText,
  setNewCommentText,
  commentFiles,
  setCommentFiles,
  projectComments,
  commentLoading,
  handleDownload,
  handleImagePreview,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpand = () => {
    if (isExpanded) {
      setIsExpanded(false);
      handlers.onCollapse(project.id);
    } else {
      setIsExpanded(true);
      handlers.onExpand(project.id);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: "hidden", transition: "all 0.2s ease", "&:hover": { boxShadow: 2, borderColor: theme.palette.primary.main } }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={600}>{project.name}</Typography>
            <Chip label={(project.status?.name || "Unknown").toUpperCase()} size="small" sx={{ backgroundColor: project.status?.color || theme.palette.primary.main, color: "#fff", fontWeight: 600, height: 24, fontSize: "0.75rem" }} />
          </Box>
        }
        subheader={<Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>{project.description || "No description"}</Typography>}
        action={<IconButton onClick={handleToggleExpand} size="small">{isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>}
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item size={{ xs: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarIcon fontSize="small" color="action" />
              <Typography variant="body2">{convertToCompanyTime(project.start_date, company?.timezone)} → {convertToCompanyTime(project.end_date, company?.timezone)}</Typography>
            </Box>
          </Grid>
          <Grid item size={{ xs: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TimeIcon fontSize="small" color="action" />
              <Typography variant="body2">{project.estimated_hours || "—"} hrs</Typography>
            </Box>
          </Grid>
          <Grid item size={{ xs: 12 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="body2">{project.client?.name || "—"}</Typography>
            </Box>
          </Grid>
          <Grid item size={{ xs: 12 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <BillingIcon fontSize="small" color="action" />
              <Typography variant="body2">{formatBillingInfo(project)}</Typography>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <GroupIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ flex: 1 }}><strong>Team:</strong> {project.assignedUsers?.length > 0 ? project.assignedUsers.map((u) => u.name).join(", ") : "—"}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <AttachFileIcon fontSize="small" color="action" />
          <Typography variant="body2"><strong>Files:</strong> {project.attachments?.length || 0}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <TimelineIcon fontSize="small" color="action" />
          <Typography variant="body2"><strong>Sprint:</strong> {project.active_sprint ? (<Chip label={project.active_sprint.name} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: 20, ml: 0.5 }} />) : (<Typography component="span" color="text.secondary">No active sprint</Typography>)}</Typography>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          {hasPermission('backlog.view') && (
            <Tooltip title="View Backlog"><IconButton size="small" onClick={() => navigate(`/projects/${project.id}/backlog`)}><TimelineIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {(hasPermission("project.view.all") || project.assignedUsers.some((u) => u.id === authUser?.id) || project.created_by === authUser?.id) && (
            <Tooltip title="View Details"><Badge color="primary"><IconButton size="small" onClick={() => handlers.onViewDetails(project)}><ViewIcon fontSize="small" /></IconButton></Badge></Tooltip>
          )}
          {hasPermission("project.board.view") && (hasPermission("project.view.all") || project.assignedUsers.some((u) => u.id === authUser?.id)) && (
            <Tooltip title="Kanban Board"><IconButton size="small" onClick={() => handlers.onViewBoard(project.id)}><DashboardIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {(hasPermission("project.update") || hasPermission("project.update.status") || hasPermission("project.manage.team")) && (project.assignedUsers.some((u) => u.id === authUser?.id) || project.created_by === authUser?.id) && (
            <Tooltip title="Edit"><IconButton size="small" onClick={() => handlers.onEdit(project)}><EditIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {hasPermission("project.delete") && (
            <Tooltip title="Delete"><IconButton size="small" onClick={() => handlers.onDelete(project.id, project.status)} color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          )}
          {hasPermission("project.comment.add") && (
            <Tooltip title={handlers.expandedProjectId === project.id ? "Hide comments" : "Show comments"}>
              <Badge badgeContent={project.comment_count || 0} color="primary">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleExpand(); }}><ChatBubbleOutlineIcon fontSize="small" /></IconButton>
              </Badge>
            </Tooltip>
          )}
        </Box>
        <Chip label={`#${String(index + 1).padStart(2, "0")}`} size="small" variant="outlined" />
      </CardActions>
      <Collapse in={isExpanded}>
        <ProjectCommentSection
          projectId={project.id}
          comments={projectComments[project.id] || []}
          loading={commentLoading[project.id]}
          onPostComment={handlers.onPostComment}
          onDeleteComment={handlers.onDeleteComment}
          users={project.assignedUsers || []}
          authUser={authUser}
          newCommentText={newCommentText}
          setNewCommentText={setNewCommentText}
          commentFiles={commentFiles}
          setCommentFiles={setCommentFiles}
          theme={theme}
          hasPermission={hasPermission}
          handleDownload={handleDownload}
          handleImagePreview={handleImagePreview}
        />
      </Collapse>
    </Card>
  );
});

export default ProjectCard;